/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { CloseOutlined } from '@ant-design/icons'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type {
  DivisionTeam,
  EntityTab,
  StartRole,
} from '@sportninja/common/features/registration/startStep/types'
import type { Division } from '@sportninja/common/types'
import { AppstoreOutlined, UnorderedListOutlined } from '@ant-design/icons'
import Text from 'src/components/ts-components/Text'
import SearchInput from 'src/components/ts-components/SearchInput'
import ViewSwitcher from 'src/components/ts-components/ViewSwitcher'
import type { ViewSwitcherOption } from 'src/components/ts-components/ViewSwitcher'
import DivisionsTab from './DivisionsTab'
import TeamsTab from './TeamsTab'
import EntityGrid from './EntityGrid'

// `EntityTab` is defined in `common/` so the competition's configured default
// tab can be resolved there too; re-exported here because this is where
// callers already import it from.
export type { EntityTab }

/** Cards carry the detail; rows carry the comparison. */
export type EntityViewMode = 'card' | 'list'

const VIEW_OPTIONS: ViewSwitcherOption<EntityViewMode>[] = [
  { value: 'card', label: capitalizeWords('Card'), icon: <AppstoreOutlined /> },
  {
    value: 'list',
    label: capitalizeWords('List'),
    icon: <UnorderedListOutlined />,
  },
]

interface Props {
  tab: EntityTab
  onTabChange: (tab: EntityTab) => void
  onSearch: (query: string) => void
  query: string
  viewMode: EntityViewMode
  onViewModeChange: (mode: EntityViewMode) => void
  /**
   * Removes the Team/Draft tab entirely, leaving Division alone. A rep
   * registers a team they are bringing rather than joining one already on the
   * list, so that half of the question is not theirs to answer — and a tab that
   * is permanently dead explains less than one that is simply absent.
   */
  teamTabDisabled?: boolean
  /** The toggle is hidden where a grid cannot render honestly. */
  isMobile?: boolean

  divisions: Division[]
  selectedDivisionId?: string | null
  onSelectDivision: (division: Division) => void
  currencyName: string
  /** Bumped when the tab changes, to restore the reader's place in the grid. */
  scrollToSelectionKey?: number

  teamResults: DivisionTeam<Division>[]
  selectedTeamId?: string | null
  onSelectTeam: (entry: DivisionTeam<Division>) => void
  /** Whether the reader is refused by a team — the step's one rule (`isTeamFullFor`). */
  isTeamFull: (entry: DivisionTeam<Division>) => boolean
  scopedDivisionName?: string | null
  onClearScope?: () => void
  /** Decides which half of each division card's fee block is relevant. */
  role: StartRole | null
}

/**
 * The "where are you registering?" question.
 *
 * A team belongs to exactly one division, so the two tabs answer the same
 * question from opposite ends: browsing narrows down to a division, while
 * searching a team resolves upward to the division that owns it. Both are
 * offered for every role — which one helps depends on what the reader already
 * knows, not on whether they are a player or a rep.
 */
const EntityZone = ({
  tab,
  onTabChange,
  onSearch,
  query,
  viewMode,
  onViewModeChange,
  teamTabDisabled = false,
  isMobile = false,
  divisions,
  selectedDivisionId,
  onSelectDivision,
  currencyName,
  scrollToSelectionKey,
  teamResults,
  selectedTeamId,
  onSelectTeam,
  isTeamFull,
  scopedDivisionName,
  onClearScope,
  role,
}: Props) => {
  const isDivisions = tab === 'divisions'

  return (
    <div css={wrapStyle}>
      {/* Tabs and the field that filters them share the top row. */}
      <div css={controlsStyle}>
        {/* Teams first, then divisions. The two are peer routes to one answer,
            not a sequence: a team resolves upward to the division that owns it
            (`handleSelectTeam`), and the teams list covers the whole
            competition until a division narrows it. No connecting arrow —
            either order is a legitimate way in, so pointing one way would
            misdescribe the other.

            A rep sees only Division: the teams tab is not theirs to answer, and
            a tab rendered permanently dead is a control that explains nothing.
            Dropping it leaves a single button, which the first/last radii
            already handle — both apply to the same element. */}
        <div css={tabsStyle} role='tablist'>
          {!teamTabDisabled && (
            <button
              type='button'
              role='tab'
              aria-selected={!isDivisions}
              css={[tabStyle, !isDivisions && tabActiveStyle]}
              onClick={() => onTabChange('teams')}
            >
              <Text
                variant='paragraphMedium'
                weight='semiBold'
                color={
                  !isDivisions ? PRIMARY_BUTTON_CONTENT.text : colors.NEUTRAL_40
                }
              >
                {capitalizeWords('Team/Draft')}
              </Text>
            </button>
          )}

          <button
            type='button'
            role='tab'
            aria-selected={isDivisions}
            css={[tabStyle, isDivisions && tabActiveStyle]}
            onClick={() => onTabChange('divisions')}
          >
            <Text
              variant='paragraphMedium'
              weight='semiBold'
              color={
                isDivisions ? PRIMARY_BUTTON_CONTENT.text : colors.NEUTRAL_40
              }
            >
              {capitalizeWords('Division')}
            </Text>
          </button>
        </div>

        <div css={searchStyle}>
          {/* Keyed by tab so switching MOUNTS a fresh field.

              SearchInput is uncontrolled — it renders from its own state and
              takes no value prop — so clearing the step's `query` on a tab
              change left the typed text sitting in the box, describing a filter
              that was no longer applied. Remounting is what actually empties
              it, without making every other caller of the shared component
              controlled. */}
          <SearchInput
            key={tab}
            onSearch={onSearch}
            placeholder={capitalizeWords(
              isDivisions ? 'Search Divisions' : 'Search Teams/Drafts'
            )}
          />
        </div>
      </div>

      {/* The results header: what is being listed on the left, how it is laid
          out on the right. Always present rather than only while filtered — a
          line that appears and disappears shifts the grid under the reader, and
          an unfiltered list saying so is worth more than silence. */}
      <div css={scopeStyle}>
        <div css={scopeTextStyle}>
          <Text
            variant='paragraphMedium'
            weight='regular'
            color={colors.NEUTRAL_40}
          >
            {isDivisions
              ? 'Showing all divisions'
              : scopedDivisionName
              ? `Showing all teams in ${scopedDivisionName}`
              : 'Showing teams from every division'}
          </Text>
          {/* Only when there is a filter to remove. The divisions tab is never
              scoped, so it never offers this. */}
          {!isDivisions && scopedDivisionName && onClearScope ? (
            <>
              {/* Separates the statement from the action so the two do not
                    read as one run-on sentence. Decorative punctuation, hidden
                    from screen readers — on the span, since Text takes a closed
                    prop set and would drop an aria-hidden passed to it. */}
              <span aria-hidden='true'>
                <Text
                  variant='paragraphMedium'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  -
                </Text>
              </span>
              <button
                type='button'
                css={clearButtonStyle}
                onClick={onClearScope}
              >
                {/* Inside the button, not beside it, so the icon and its label
                    are one hit target rather than two controls doing the same
                    job. Decorative — the label already names the action. */}
                <CloseOutlined css={clearIconStyle} aria-hidden='true' />
                <Text
                  variant='paragraphMedium'
                  weight='semiBold'
                  color={colors.PRIMARY}
                >
                  Remove Filter
                </Text>
              </button>
            </>
          ) : null}
        </div>

        {/* Absent on a phone, where the grid has no honest rendering and the
            mode is forced to cards. */}
        {!isMobile ? (
          <ViewSwitcher<EntityViewMode>
            mode={viewMode}
            onChange={onViewModeChange}
            options={VIEW_OPTIONS}
            ariaLabel={capitalizeWords('Result Layout')}
          />
        ) : null}
      </div>

      {viewMode === 'list' ? (
        <EntityGrid
          tab={tab}
          role={role}
          currencyName={currencyName}
          divisions={divisions}
          selectedDivisionId={selectedDivisionId}
          onSelectDivision={onSelectDivision}
          teamResults={teamResults}
          selectedTeamId={selectedTeamId}
          onSelectTeam={onSelectTeam}
          isTeamFull={isTeamFull}
        />
      ) : isDivisions ? (
        <DivisionsTab
          divisions={divisions}
          selectedDivisionId={selectedDivisionId}
          onSelect={onSelectDivision}
          currencyName={currencyName}
          scrollToSelectionKey={scrollToSelectionKey}
          role={role}
        />
      ) : (
        <TeamsTab
          results={teamResults}
          selectedTeamId={selectedTeamId}
          onSelect={onSelectTeam}
          isTeamFull={isTeamFull}
          scopedDivisionName={scopedDivisionName}
          hasQuery={Boolean(query.trim())}
          scrollToSelectionKey={scrollToSelectionKey}
        />
      )}
    </div>
  )
}

export default EntityZone

/* The three stacked rows — tab group, search field, results. One `gap` spaces
   all of them, so a row being swapped or removed cannot leave a stray margin
   behind. */
const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

/* The group sizes to its two tabs and does not stretch — the search field
   beside it takes the rest of the line. `overflow: hidden` clips the selected
   tab's PRIMARY fill to the rounded corners, so the fill meets the frame
   cleanly at either end.
   The border matches the search field's rather than the old NEUTRAL_600: a
   heavier grey now reads as a stray outline around the amber fill. */
const tabsStyle = css`
  display: inline-flex;
  flex: none;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
  overflow: hidden;

  /* Narrow: the group takes the full line and the two tabs split it evenly,
     rather than sitting at their content width against a wrapped search. */
  @media (max-width: 768px) {
    display: flex;
    width: 100%;
  }
`

/* 40px tall, matching the search field beside it and the house control height.
   The wide padding is what gives the pair enough presence to read as the live
   control of this step rather than a caption above the grid. */
const tabStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 8px 32px;
  border: 0;
  background: none;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;

  @media (max-width: 768px) {
    flex: 1;
    padding: 8px 16px;
  }
`

/* The selected tab wears the same fill as a selected role card — the tenant
   PRIMARY, with its label taking PRIMARY_BUTTON_CONTENT.text so it stays
   readable on both the SportNinja amber and the Canlan blue. */
const tabActiveStyle = css`
  background-color: ${colors.PRIMARY};
`

/* Tabs on the left, search taking the rest of the line, wrapping to two lines
   when narrow. */
const controlsStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
`

/* The results header. What is listed sits left, the layout toggle right; on a
   narrow line the toggle wraps under the text rather than squeezing it. */
const scopeStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
  min-width: 0;
`

/* The statement and its Remove Filter action, which read as one sentence and so
   wrap together rather than being split across the row. */
const scopeTextStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
`

/* The icon and its label read as one control, so they sit in the button
   together with a small gap rather than the icon floating beside it. */
const clearButtonStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`

/* antd icons render as glyphs, so font-size IS their size control — this is
   icon sizing, not type. Matches the PRIMARY label it sits beside. */
const clearIconStyle = css`
  font-size: 12px;
  color: ${colors.PRIMARY};
`

/* The house form field, matching TextInput and the registration date/time
   fields: 40px tall, 8px radius, SECONDARY_100 border on SECONDARY_200. The
   shared SearchInput paints its own borderless chrome inside, so the frame
   lives here on the wrapper. */
const searchStyle = css`
  /* Right-hand item of the controls row. It takes whatever the line leaves
     after the tab group rather than holding a fixed width — searching is the
     fast path into a long division list, so it gets the room. It never drops
     below the width it had before, so a narrow line wraps instead of squeezing
     the field. */
  flex: 1;
  min-width: 320px;
  max-width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
  background-color: ${colors.SECONDARY_200};
  overflow: hidden;

  /* SearchInput paints its own SECONDARY_400 chrome and a 4px radius, which
     would sit inside this frame as a mismatched inner block. Its own border is
     already none, so only the fills need clearing — the wrapper is the field. */
  .ant-input-wrapper,
  .ant-input-group-addon,
  .ant-input-affix-wrapper,
  .ant-input-outlined,
  .ant-input,
  .ant-input-search-button {
    background-color: transparent !important;
    border-radius: 0;
  }

  /* SearchInput sets NEUTRAL_100 — the disabled-text token — which made an
     empty field read as switched off. Corrected here rather than in the shared
     component so no other caller's appearance changes. */
  .ant-input,
  .ant-input-affix-wrapper {
    color: ${colors.NEUTRAL_0};
  }

  .ant-input::placeholder {
    color: ${colors.NEUTRAL_50};
  }

  .anticon {
    color: ${colors.NEUTRAL_50};
  }

  @media (max-width: 768px) {
    min-width: 0;
    width: 100%;
  }
`
